<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTenant, hasActiveSubscription } from '@/composables/useTenant';
import { useAuth } from '@/composables/useAuth';
import { membersApi } from '@/services/api';
import type { TenantTier, TenantMember, UserRole } from '@/types/models';
import AppBadge from '@/components/ui/AppBadge.vue';
import MemberInviteDrawer from '@/components/MemberInviteDrawer.vue';
import {
  NInput,
  NButton,
  NTabs,
  NTabPane,
  NSelect,
  NPopconfirm,
  NTooltip,
  useMessage,
} from 'naive-ui';
import {
  Building2,
  Key,
  Check,
  CreditCard,
  Users,
  UserPlus,
  UserX,
  ShieldCheck,
  RefreshCw,
  Lock,
} from 'lucide-vue-next';

const { t } = useI18n();
const naiveMsg = useMessage();
const { activeTenant, updateTenantMetadata, currentOrgTeams } = useTenant();
const { canManageMembers } = useAuth();

const activeTab = ref<'organization' | 'members' | 'api'>('organization');
const saved = ref(false);
const isLoadingMembers = ref(false);
const showInviteDrawer = ref(false);

const form = ref({
  name: activeTenant.value.name,
  tier: (activeTenant.value.tier || activeTenant.value.type || 'PRO') as TenantTier,
  sportType: activeTenant.value.sportType || 'baseball',
  seasonYear: activeTenant.value.seasonYear || '2025年 公式戦',
});

const members = ref<TenantMember[]>([]);

watch(
  activeTenant,
  (newTenant) => {
    if (newTenant) {
      form.value.name = newTenant.name;
      form.value.tier = (newTenant.tier || newTenant.type || 'COLLEGIATE') as TenantTier;
      form.value.sportType = newTenant.sportType || 'baseball';
      form.value.seasonYear = newTenant.seasonYear || '2025 Spring';
    }
  },
  { immediate: true }
);

const tenantTierOptions = computed<{ label: string; value: TenantTier }[]>(() => [
  { label: '都市対抗・社会人 (PRO / JABA)', value: 'PRO' },
  { label: '大学野球連盟 (COLLEGIATE / Big6)', value: 'COLLEGIATE' },
  { label: 'リトルシニア・中学硬式 (AMATEUR / Senior)', value: 'AMATEUR' },
  { label: 'リトルリーグ・学童 (ACADEMIC / Little League)', value: 'ACADEMIC' },
  { label: 'その他連盟 (OTHERS)', value: 'OTHERS' },
]);

const roleOptions: { label: string; value: UserRole }[] = [
  { label: 'ADMIN', value: 'ADMIN' },
  { label: 'COACH', value: 'COACH' },
  { label: 'PLAYER', value: 'PLAYER' },
  { label: 'GUARDIAN', value: 'GUARDIAN' },
];

const teamSelectOptions = computed(() => {
  return currentOrgTeams.value.map(t => ({ label: t.name, value: t.id }));
});

// Member counts & Last Admin Guardrail computation
const adminCount = computed(() => {
  return members.value.filter(m => m.role === 'ADMIN' && m.isActive !== false).length;
});

const isLastAdmin = (member: TenantMember) => {
  return member.role === 'ADMIN' && adminCount.value <= 1;
};

const loadMembers = async () => {
  isLoadingMembers.value = true;
  try {
    const list = await membersApi.getMembers(activeTenant.value.id);
    members.value = list || [];
  } catch (err: any) {
    naiveMsg.error(err.message || 'Failed to load organization members');
  } finally {
    isLoadingMembers.value = false;
  }
};

onMounted(() => {
  loadMembers();
});

const handleSave = () => {
  updateTenantMetadata({
    name: form.value.name,
    tier: form.value.tier,
    type: form.value.tier,
    sportType: form.value.sportType,
    seasonYear: form.value.seasonYear,
  });
  saved.value = true;
  naiveMsg.success(t('settings.savedMsg'));
  setTimeout(() => (saved.value = false), 3000);
};

// PATCH /api/v1/tenants/members/:memberId
const handleRoleChange = async (member: TenantMember, newRole: UserRole) => {
  if (isLastAdmin(member) && newRole !== 'ADMIN') {
    naiveMsg.error(t('settings.lastAdminTooltip'));
    return;
  }

  try {
    const updated = await membersApi.updateMemberRole(member.id, newRole, activeTenant.value.id);
    member.role = updated.role;
    naiveMsg.success(`Updated role to ${newRole} for ${member.fullName}`);
  } catch (err: any) {
    naiveMsg.error(err.message || 'Failed to update member role');
    await loadMembers();
  }
};

// DELETE /api/v1/tenants/members/:memberId (Soft deletion: Revoke Tenant Access)
const handleRevokeAccess = async (member: TenantMember) => {
  if (isLastAdmin(member)) {
    naiveMsg.error(t('settings.revokeTooltip'));
    return;
  }

  try {
    await membersApi.removeMember(member.id, activeTenant.value.id);
    members.value = members.value.filter(m => m.id !== member.id);
    naiveMsg.info(t('settings.revokeConfirm', { name: member.fullName }));
  } catch (err: any) {
    naiveMsg.error(err.message || 'Failed to revoke member access');
  }
};
</script>

<template>
  <div class="max-w-5xl space-y-8 animate-in fade-in duration-200">
    <!-- Header -->
    <div class="pb-2 border-b border-slate-800/80">
      <div class="flex items-center gap-2">
        <h1 class="text-2xl sm:text-3xl font-extrabold text-slate-100 tracking-tight">
          {{ t('settings.title') }}
        </h1>
        <AppBadge variant="accent">
          {{ activeTenant.tier || activeTenant.type || 'COLLEGIATE' }}
        </AppBadge>
      </div>
      <p class="mt-1 text-xs sm:text-sm text-slate-400">
        {{ t('settings.subtitle', { name: activeTenant.name }) }} &bull; Tenant UUID: <span class="font-mono text-slate-300">{{ activeTenant.id }}</span>
      </p>
    </div>

    <!-- Navigation Tabs -->
    <NTabs v-model:value="activeTab" type="line" animated>
      <!-- TAB 1: Organization Settings -->
      <NTabPane name="organization" :tab="t('settings.tabConfig')">
        <div class="space-y-6 pt-4">
          <!-- Tenant Details Card -->
          <div class="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-6">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-2xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center text-sky-400">
                  <Building2 class="w-5 h-5" />
                </div>
                <div>
                  <h2 class="text-base font-bold text-slate-100">{{ t('settings.metadata') }}</h2>
                  <p class="text-xs text-slate-400">{{ t('settings.apiScopingSubtitle') }}</p>
                </div>
              </div>
              <AppBadge variant="accent">{{ form.tier }}</AppBadge>
            </div>

            <!-- Subscription Status in Settings -->
            <div class="p-4 rounded-2xl bg-slate-950 border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
              <div class="flex items-center gap-2.5">
                <CreditCard class="w-4 h-4 text-sky-400" />
                <span class="text-slate-300">Subscription Status:</span>
                <span :class="['font-bold font-mono px-2 py-0.5 rounded text-[10px]', hasActiveSubscription ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300']">
                  {{ hasActiveSubscription ? 'ACTIVE' : 'NO ACTIVE SERVICES' }}
                </span>
              </div>
              <router-link to="/dashboard/billing" class="text-sky-400 hover:text-sky-300 font-bold underline text-xs">
                Manage Subscriptions &rarr;
              </router-link>
            </div>

            <form @submit.prevent="handleSave" class="space-y-4 pt-2">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('settings.orgName') }}</label>
                  <NInput
                    v-model:value="form.name"
                    size="medium"
                  />
                </div>
                <div>
                  <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('settings.classificationTier') }}</label>
                  <NSelect
                    v-model:value="form.tier"
                    :options="tenantTierOptions"
                    size="medium"
                  />
                </div>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('settings.sportType') }}</label>
                  <NInput
                    v-model:value="form.sportType"
                    size="medium"
                  />
                </div>
                <div>
                  <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('settings.seasonYear') }}</label>
                  <NInput
                    v-model:value="form.seasonYear"
                    size="medium"
                  />
                </div>
              </div>

              <div class="flex items-center justify-between pt-4 border-t border-slate-800">
                <span v-if="saved" class="text-xs text-emerald-400 font-semibold flex items-center gap-1">
                  <Check class="w-4 h-4" /> {{ t('settings.savedMsg') }}
                </span>
                <span v-else />

                <NButton
                  type="primary"
                  attr-type="submit"
                  class="font-bold text-xs"
                >
                  {{ t('settings.saveChanges') }}
                </NButton>
              </div>
            </form>
          </div>
        </div>
      </NTabPane>

      <!-- TAB 2: Member Management (RBAC & Last Admin Guardrail) -->
      <NTabPane name="members" :tab="t('settings.tabMembers')">
        <div class="space-y-6 pt-4">
          <div class="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-5">
            <!-- Header Bar -->
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-800">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-2xl bg-sky-500/15 border border-sky-500/30 flex items-center justify-center text-sky-400">
                  <Users class="w-5 h-5" />
                </div>
                <div>
                  <h3 class="text-base font-extrabold text-slate-100">
                    {{ t('settings.membersTitle') }}
                  </h3>
                  <p class="text-xs text-slate-400">
                    {{ t('settings.membersSubtitle') }}
                  </p>
                </div>
              </div>

              <div class="flex items-center gap-2.5">
                <NButton size="small" secondary @click="loadMembers" :loading="isLoadingMembers">
                  <template #icon><RefreshCw class="w-3.5 h-3.5" /></template>
                  Refresh
                </NButton>

                <NButton
                  v-if="canManageMembers"
                  type="primary"
                  size="small"
                  @click="showInviteDrawer = true"
                  class="font-bold text-xs shadow-md"
                >
                  <template #icon><UserPlus class="w-4 h-4" /></template>
                  {{ t('memberInvite.drawerTitle') }}
                </NButton>
              </div>
            </div>

            <!-- Last Admin Guardrail Notice -->
            <div class="p-3.5 rounded-2xl bg-slate-950 border border-slate-800 flex items-center justify-between text-xs">
              <div class="flex items-center gap-2 text-slate-300">
                <ShieldCheck class="w-4 h-4 text-emerald-400" />
                <span>{{ t('settings.lastAdminNotice', { count: adminCount }) }}</span>
              </div>
              <span class="text-[10px] text-slate-500 uppercase font-bold tracking-wider">{{ t('settings.softDeletionMode') }}</span>
            </div>

            <!-- Members Table -->
            <div class="border border-slate-800 rounded-2xl overflow-hidden bg-slate-950">
              <div class="overflow-x-auto">
                <table class="w-full text-xs text-left border-collapse">
                  <thead>
                    <tr class="border-b border-slate-800/80 bg-slate-950 text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                      <th class="py-3 px-4">{{ t('settings.colMemberName') }}</th>
                      <th class="py-3 px-4">{{ t('settings.colEmail') }}</th>
                      <th class="py-3 px-4">{{ t('settings.colAssignedTeam') }}</th>
                      <th class="py-3 px-4">{{ t('settings.colRole') }}</th>
                      <th class="py-3 px-4 text-right">{{ t('settings.colAccess') }}</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-800/60">
                    <tr v-if="members.length === 0">
                      <td colspan="5" class="py-8 text-center text-slate-500 italic">
                        No members found in this tenant.
                      </td>
                    </tr>
                    <tr
                      v-for="member in members"
                      :key="member.id"
                      class="hover:bg-slate-900/60 transition"
                    >
                      <td class="py-3 px-4">
                        <div class="font-bold text-slate-200">{{ member.fullName }}</div>
                        <div v-if="isLastAdmin(member)" class="text-[10px] text-amber-400 font-semibold flex items-center gap-1 mt-0.5">
                          <Lock class="w-3 h-3" /> {{ t('settings.lastAdminBadge') }}
                        </div>
                      </td>

                      <td class="py-3 px-4 font-mono text-slate-400">
                        {{ member.email }}
                      </td>

                      <td class="py-3 px-4 text-slate-300">
                        {{ member.teamName || t('settings.unassigned') }}
                      </td>

                      <td class="py-3 px-4">
                        <!-- Inline Role Switcher with Last Admin Guardrail -->
                        <div class="w-36">
                          <NTooltip v-if="isLastAdmin(member)" trigger="hover">
                            <template #trigger>
                              <div class="opacity-75">
                                <NSelect
                                  :value="member.role"
                                  :options="roleOptions"
                                  size="small"
                                  disabled
                                />
                              </div>
                            </template>
                            {{ t('settings.lastAdminTooltip') }}
                          </NTooltip>

                          <NSelect
                            v-else
                            :value="member.role"
                            :options="roleOptions"
                            size="small"
                            @update:value="(val) => handleRoleChange(member, val as UserRole)"
                          />
                        </div>
                      </td>

                      <td class="py-3 px-4 text-right">
                        <!-- Revoke Access Button (Soft Deletion semantics) -->
                        <NTooltip v-if="isLastAdmin(member)" trigger="hover">
                          <template #trigger>
                            <NButton
                              size="tiny"
                              quaternary
                              disabled
                              class="text-slate-600 text-xs"
                            >
                              {{ t('settings.revokeBtn') }}
                            </NButton>
                          </template>
                          {{ t('settings.revokeTooltip') }}
                        </NTooltip>

                        <NPopconfirm
                          v-else
                          @positive-click="handleRevokeAccess(member)"
                          :positive-text="t('settings.revokeBtn')"
                          :negative-text="t('common.cancel')"
                        >
                          <template #trigger>
                            <NButton
                              size="tiny"
                              quaternary
                              type="error"
                              class="text-xs"
                            >
                              <template #icon><UserX class="w-3.5 h-3.5" /></template>
                              {{ t('settings.revokeBtn') }}
                            </NButton>
                          </template>
                          {{ t('settings.revokeConfirm', { name: member.fullName }) }}
                        </NPopconfirm>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </NTabPane>

      <!-- TAB 3: API & Context Inspector -->
      <NTabPane name="api" :tab="t('settings.tabApi')">
        <div class="space-y-6 pt-4">
          <div class="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-4">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
                <Key class="w-5 h-5" />
              </div>
              <div>
                <h2 class="text-base font-bold text-slate-100">{{ t('settings.apiScopingTitle') }}</h2>
                <p class="text-xs text-slate-400">{{ t('settings.apiScopingSubtitle') }}</p>
              </div>
            </div>

            <div class="bg-slate-950 p-4 rounded-2xl border border-slate-800/80 font-mono text-[11px] text-slate-400 space-y-2">
              <div class="text-sky-400 font-bold">X-Tenant-Id: {{ activeTenant.id }}</div>
              <div class="text-slate-300">Authorization: Bearer &lt;clerk_jwt_session_token&gt;</div>
              <div class="text-slate-300">Content-Type: application/json</div>
              <div class="text-slate-300">Accept: application/json</div>
            </div>

            <p class="text-xs text-slate-400">
              {{ t('settings.apiScopingDesc') }}
            </p>
          </div>
        </div>
      </NTabPane>
    </NTabs>

    <!-- Member Invite Drawer Component -->
    <MemberInviteDrawer
      v-model:show="showInviteDrawer"
      :team-options="teamSelectOptions"
      @invited="loadMembers"
    />
  </div>
</template>
